// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

export interface PluralizeOptions {
  singular: string;
  plural?: string;
  format?: Intl.NumberFormatOptions;
  includeCount?: boolean;
}

/**
 * Returns a pluralized string representing an amount of items.
 *
 * @param count - A number representing an amount
 * @param options.singular - The singular form of the noun.
 * @param options.plural - The plural form of the noun. If not provided, an "s"
 * will be appended to the singular form.
 * @param options.format - Intl.NumberFormatOptions to format the count.
 * @param options.includeCount - If true, the count will be included in the
 * output. Defaults to true.
 */
export function pluralize(count: number, options: PluralizeOptions) {
  const includeCount = options.includeCount ?? true;
  const formattedCount = count.toLocaleString(undefined, options.format);
  const formattedLabel =
    count === 1 ? options.singular : (options.plural ?? `${options.singular}s`);

  if (includeCount) {
    return `${formattedCount} ${formattedLabel}`;
  }

  return formattedLabel;
}
